import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconSearch } from "@tabler/icons-react";

import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { usePinnableParameters } from "@/hooks/useLabInsights";
import { PinnableParameter } from "@/types/lab-insights";

import styles from "./EditPinsDrawer.module.css";

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

interface EditPinsDrawerProps {
  open: boolean;
  memberId: string | null | undefined;
  /**
   * Parameter ids the member currently has pinned. Rows whose id is in
   * this set are rendered with the locked `.rowPinned` styling (Figma
   * #EDE8F5 tint, muted text, cursor not-allowed) and don't fire select —
   * the user can only pick from the UNPINNED set when adding/replacing a
   * pin. The parent decides which existing pin to displace based on the
   * grid slot the user clicked Edit on, not on the drawer's selection.
   */
  currentPinIds: string[];
  onClose: () => void;
  onSave: (canonicalParameterIds: string[]) => Promise<unknown> | unknown;
}

const firstLetterOf = (name: string): string => {
  const ch = name.trim().charAt(0).toUpperCase();
  return /[A-Z]/.test(ch) ? ch : "#";
};

export const EditPinsDrawer: React.FC<EditPinsDrawerProps> = ({
  open,
  memberId,
  currentPinIds,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { parameters, loading } = usePinnableParameters(memberId, open);

  const [query, setQuery] = useState("");
  // Single-select — the user picks ONE unpinned parameter to add into the
  // pin slot they clicked Edit on. selectedId starts null and only flips to
  // a real id once the user taps an unpinned row. Tapping the same row
  // again clears it so they can bail without closing the drawer.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const letterAnchorRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Pinned ids as a Set for O(1) lookup in the render loop.
  const pinnedIdSet = useMemo(
    () => new Set(currentPinIds),
    [currentPinIds]
  );

  // Reset the local selection / search every time the drawer reopens. We
  // intentionally do NOT pre-select any row — the currently-pinned rows are
  // shown as locked-and-tinted instead, and the user picks an unpinned row
  // to commit a replacement on Done.
  useEffect(() => {
    if (open) {
      setSelectedId(null);
      setQuery("");
    }
  }, [open]);

  const filtered: PinnableParameter[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return parameters;
    return parameters.filter((p) =>
      p.canonicalName.toLowerCase().includes(q)
    );
  }, [parameters, query]);

  // Pre-bucket by first letter for the A-Z navigator.
  const lettersInList = useMemo(() => {
    const set = new Set<string>();
    for (const p of filtered) set.add(firstLetterOf(p.canonicalName));
    return set;
  }, [filtered]);

  // Radio-style select: tapping an unpinned row replaces the prior
  // selection; tapping the same one again clears it. Pinned rows are
  // locked and never enter the selection.
  const select = (id: string) => {
    if (pinnedIdSet.has(id)) return;
    setSelectedId((prev) => (prev === id ? null : id));
  };

  const handleAlphaTap = (letter: string) => {
    const node = letterAnchorRefs.current[letter];
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleDone = async () => {
    if (!selectedId) {
      showToast({
        message: t("labInsights.pinAtLeastOne"),
        variant: "info",
      });
      return;
    }
    try {
      setSaving(true);
      await onSave([selectedId]);
      onClose();
    } catch (err) {
      showToast({
        message: t("labInsights.pinSaveFailed"),
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Build the alphabetical groupings while preserving filtered order. Each
  // letter section's first entry gets a ref so the A-Z navigator can scroll
  // to it. Three row states (per Figma 2739:24255 + product follow-up):
  //   - .rowPinned    — id is in currentPinIds (already pinned). Lavender
  //                     #EDE8F5 tint, muted text, cursor not-allowed,
  //                     disabled. Clicking does nothing.
  //   - .rowSelected  — id matches the user's current drawer pick. Same
  //                     lavender tint but with active text/cursor.
  //   - default       — unpinned, not yet picked. Plain.
  const renderRows = (): React.ReactNode[] => {
    const seen = new Set<string>();
    return filtered.map((p) => {
      const letter = firstLetterOf(p.canonicalName);
      const isFirstOfLetter = !seen.has(letter);
      if (isFirstOfLetter) seen.add(letter);
      const isPinned = pinnedIdSet.has(p.canonicalParameterId);
      const isSelected = selectedId === p.canonicalParameterId;

      return (
        <div
          key={p.canonicalParameterId}
          ref={(node) => {
            if (isFirstOfLetter) letterAnchorRefs.current[letter] = node;
          }}
        >
          <button
            type="button"
            className={[
              styles.row,
              isPinned ? styles.rowPinned : "",
              isSelected ? styles.rowSelected : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={isPinned ? undefined : () => select(p.canonicalParameterId)}
            disabled={isPinned}
            role="radio"
            aria-checked={isSelected}
            aria-disabled={isPinned}
          >
            <span className={styles.rowName} title={p.canonicalName}>
              {p.canonicalName}
            </span>
          </button>
        </div>
      );
    });
  };

  return (
    <FooterDrawer
      open={open}
      onClose={onClose}
      title={t("labInsights.selectParameter")}
      doneText={t("common.done")}
      onDone={handleDone}
      // Done is gated on (a) a save not already in flight and (b) the user
      // having actually picked an unpinned row. selectedId stays null on
      // open because no row is pre-selected anymore (currentPinIds locks
      // the pinned rows); the user must tap something else first.
      doneDisabled={saving || !selectedId}
      donePosition="top"
      height="85vh"
      allowKeyboard
    >
      <div className={styles.body}>
        <div className={styles.searchRow}>
          <div className={styles.searchInput}>
            <input
              className={styles.searchInputField}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("labInsights.searchParameter")}
              autoComplete="off"
            />
            <span className={styles.searchIcon} aria-hidden>
              <IconSearch size={16} stroke={1.75} />
            </span>
          </div>
        </div>

        <div className={styles.listWrapper}>
          <div className={styles.list} ref={listContainerRef}>
            {loading && parameters.length === 0 && (
              <div className={styles.emptyState}>
                {t("labInsights.loading")}
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className={styles.emptyState}>
                {t("labInsights.noPinnableParameters")}
              </div>
            )}
            {filtered.length > 0 && renderRows()}
          </div>

          <div className={styles.alphaBar} aria-hidden>
            {ALPHABET.map((letter) => {
              const enabled = lettersInList.has(letter);
              return (
                <span
                  key={letter}
                  className={[
                    styles.alphaLetter,
                    enabled ? styles["alphaLetter--active"] : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={enabled ? () => handleAlphaTap(letter) : undefined}
                >
                  {letter}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </FooterDrawer>
  );
};

export default EditPinsDrawer;
